import { useEffect, useState } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import SearchFilters from '../components/SearchFilters';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Search, Star, MapPin, Clock, MessageSquare, Coins, Flag, Crown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReportUserDialog from '../components/ReportUserDialog';
import VideoCallButton from '../components/VideoCallButton';

export default function BrowseUsers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showDonateDialog, setShowDonateDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [hours, setHours] = useState(1);
  const [description, setDescription] = useState('');
  const [donateCredits, setDonateCredits] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(u => 
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.profile.skills_offered.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      // Build query params from filters
      const params = new URLSearchParams();
      if (filters.skill) params.append('skill', filters.skill);
      if (filters.location) params.append('location', filters.location);
      if (filters.minRating) params.append('min_rating', filters.minRating.toString());
      if (filters.trustLevel) params.append('trust_level', filters.trustLevel);
      if (filters.availability) params.append('availability', filters.availability);
      if (filters.sortBy) params.append('sort_by', filters.sortBy);
      
      const endpoint = Object.keys(filters).length > 0 && Object.values(filters).some(v => v)
        ? `/users/search?${params.toString()}`
        : '/users?limit=50';
      
      const response = await axios.get(`${API}${endpoint}`, { withCredentials: true });
      const otherUsers = response.data.filter(u => u.id !== user?.id);
      setUsers(otherUsers);
      setFilteredUsers(otherUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('errors.generic'));
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleRequestTime = async () => {
    if (!description.trim() || hours <= 0) {
      toast.error(t('errors.invalidInput'));
      return;
    }

    if (hours > user.credits_balance) {
      toast.error(t('errors.insufficientCredits'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/transactions`, {
        provider_user_id: selectedUser.id,
        hours: parseFloat(hours),
        description
      }, { withCredentials: true });

      toast.success(t('notifications.requestSent'));
      setShowRequestDialog(false);
      setDescription('');
      setHours(1);
    } catch (error) {
      console.error('Error creating transaction:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleDonateCredits = async () => {
    if (donateCredits <= 0) {
      toast.error(t('errors.invalidInput'));
      return;
    }

    if (donateCredits > user.credits_balance) {
      toast.error(t('errors.insufficientCredits'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/donations/credits`, {
        recipient_id: selectedUser.id,
        credits: parseInt(donateCredits)
      }, { withCredentials: true });

      toast.success(t('notifications.donationSent'), {
        description: `${donateCredits} ${t('common.credits')} → ${selectedUser.name}`
      });
      
      setShowDonateDialog(false);
      setDonateCredits(1);
      
      // Refresh to update balance
      window.location.reload();
    } catch (error) {
      console.error('Error donating credits:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };


  const getTrustLevelColor = (level) => {
    const colors = {
      'newcomer': 'bg-gray-100 text-gray-600 border-gray-300',
      'trusted': 'bg-blue-100 text-blue-600 border-blue-300',
      'expert': 'bg-purple-100 text-purple-600 border-purple-300'
    };
    return colors[level] || colors.newcomer;
  };

  return (
    <Layout>
      <div data-testid="browse-users-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">{t('browse.title')}</h1>
        </div>

        {/* Advanced Search Filters */}
        <SearchFilters onFilterChange={handleFilterChange} resultsCount={filteredUsers.length} />

        {/* Search Bar */}
        <Card className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <Input
              data-testid="search-users-input"
              type="text"
              placeholder={t('browse.search')}
              className="pl-10 text-base"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </Card>

        {/* Users Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUsers.map((userItem) => (
            <Card key={userItem.id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={userItem.picture} />
                  <AvatarFallback className="bg-teal-100 text-teal-600 text-xl">
                    {userItem.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white">{userItem.name}</h3>
                    {userItem.creator_status && (
                      <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 gap-1">
                        <Crown className="h-3 w-3" />
                        Creator
                      </Badge>
                    )}
                    {userItem.premium_status && !userItem.creator_status && (
                      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300 gap-1">
                        <Sparkles className="h-3 w-3" />
                        Premium
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">@{userItem.username || userItem.email.split('@')[0]}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-sm font-medium ml-1">{userItem.reputation.rating_avg.toFixed(1)}</span>
                    </div>
                    <span className="text-gray-300">•</span>
                    <span className={`text-xs px-2 py-1 rounded-full border ${getTrustLevelColor(userItem.reputation.trust_level)}`}>
                      {userItem.reputation.trust_level}
                    </span>
                  </div>
                </div>
              </div>

              {userItem.profile.location && (
                <div className="flex items-center text-gray-600 text-sm mb-3">
                  <MapPin className="h-4 w-4 mr-1" />
                  {userItem.profile.location}
                </div>
              )}

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">{t('browse.skillsOffered')}</p>
                <div className="flex flex-wrap gap-2">
                  {userItem.profile.skills_offered.length > 0 ? (
                    userItem.profile.skills_offered.slice(0, 3).map((skill, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-teal-50 text-teal-600">
                        {skill}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-gray-400">{t('browse.noSkills')}</span>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  data-testid={`request-time-${userItem.id}`}
                  onClick={() => {
                    setSelectedUser(userItem);
                    setShowRequestDialog(true);
                  }}
                  className="flex-1 bg-teal-500 hover:bg-teal-600 text-white rounded-lg"
                >
                  <Clock className="mr-2 h-4 w-4" />
                  {t('browse.requestTime')}
                </Button>
                <Button
                  data-testid={`donate-${userItem.id}`}
                  variant="outline"
                  onClick={() => {
                    setSelectedUser(userItem);
                    setShowDonateDialog(true);
                  }}
                  className="border-slate-400 text-slate-600 hover:bg-slate-50 rounded-lg"
                  title={t('donate.donateCredits')}
                >
                  <Coins className="h-4 w-4" />
                </Button>
                <Button
                  data-testid={`message-${userItem.id}`}
                  variant="outline"
                  onClick={() => navigate(`/messages?user=${userItem.id}`)}
                  className="border-teal-500 text-teal-500 hover:bg-teal-50 rounded-lg"
                >
                  <MessageSquare className="h-4 w-4" />
                </Button>
                <Button
                  data-testid={`report-${userItem.id}`}
                  variant="outline"
                  onClick={() => {
                    setSelectedUser(userItem);
                    setShowReportDialog(true);
                  }}
                  className="border-red-400 text-red-500 hover:bg-red-50 rounded-lg"
                  title={t('report.reportUser')}
                >
                  <Flag className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Video/Voice Call Buttons */}
              <div className="mt-3">
                <VideoCallButton otherUserId={userItem.id} otherUserName={userItem.name} />
              </div>
            </Card>
          ))}
        </div>

        {filteredUsers.length === 0 && (
          <Card className="p-12 text-center">
            <p className="text-gray-500">{t('browse.noUsersFound')}</p>
          </Card>
        )}
      </div>

      {/* Request Time Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent data-testid="request-time-dialog">
          <DialogHeader>
            <DialogTitle>{t('browse.requestDialog.title')} {selectedUser?.name}</DialogTitle>
            <DialogDescription>
              {t('browse.requestDialog.subtitle', { credits: user?.credits_balance })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('browse.requestDialog.hoursNeeded')}</label>
              <Input
                data-testid="hours-input"
                type="number"
                min="0.5"
                step="0.5"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('browse.requestDialog.description')}</label>
              <Textarea
                data-testid="description-input"
                rows={4}
                placeholder={t('browse.requestDialog.descPlaceholder')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>{t('browse.requestDialog.cancel')}</Button>
            <Button
              data-testid="send-request-button"
              onClick={handleRequestTime}
              disabled={loading}
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              {loading ? t('browse.requestDialog.sending') : t('browse.requestDialog.send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Donate Credits Dialog */}
      <Dialog open={showDonateDialog} onOpenChange={setShowDonateDialog}>
        <DialogContent data-testid="donate-dialog">
          <DialogHeader>
            <DialogTitle>{t('donate.donateCreditsTo')} {selectedUser?.name || ''}</DialogTitle>
            <DialogDescription>
              {user?.credits_balance !== undefined && t('donate.subtitle', { credits: user.credits_balance })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-600 rounded-lg p-4 text-center">
              <Coins className="h-8 w-8 mx-auto mb-2 text-slate-600 dark:text-slate-400" />
              <p className="text-sm text-slate-600 dark:text-slate-300">{t('donate.helpMessage')}</p>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block dark:text-white">{t('donate.creditsAmount')}</label>
              <Input
                data-testid="donate-credits-input"
                type="number"
                min="1"
                max={user?.credits_balance || 0}
                value={donateCredits}
                onChange={(e) => setDonateCredits(e.target.value)}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('donate.available')}: {user?.credits_balance} {t('common.credits')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDonateDialog(false)}>{t('common.cancel')}</Button>
            <Button
              data-testid="send-donation-button"
              onClick={handleDonateCredits}
              disabled={loading}
              className="bg-slate-500 hover:bg-slate-600 text-white"
            >
              {loading ? t('common.processing') : t('donate.sendDonation')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report User Dialog */}
      <ReportUserDialog 
        user={selectedUser}
        open={showReportDialog}
        onOpenChange={setShowReportDialog}
      />
    </Layout>
  );
}